import {Database, RpcReturnTypeT, RpcSortOptionT, useRpcQuery} from '@tnc/shared';
import clsx from 'clsx';
import {debounce} from 'lodash';
import React, {ChangeEvent, Ref, useCallback, useImperativeHandle, useMemo, useRef, useState} from 'react';
import ReactPaginate from 'react-paginate';
import {twMerge} from 'tailwind-merge';

import {ReactPaginateClickEventT, VALID_LIMITS, limitT} from '@/lib/types/pagination.types';
import {RpcDataTablePropsT, RpcFunctionNameT} from '@/lib/types/rpcDataTable.types';

import {LoadingSpinningLogoComponent} from './loadingSpinningLogoComponent';

export type DataTableRpcRefT = {
  refetch: () => void;
  setLimit: (limit: limitT) => void;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
};

function DataTableRpcComponent<F extends RpcFunctionNameT>(props: RpcDataTablePropsT<F>, ref: Ref<DataTableRpcRefT>) {
  const {
    functionName,
    functionArgs,
    columns,
    filters = [],
    actions,
    sortOptions = [],
    limit: initialLimit = 5,
    enabled = true,
    title,
    className,
    hideRefresh = false,
    hideSort = false,
    hideTitle = false,
    hideShowing = false,
    hideLimit = false,
    hideLimitIcon = false,
    hidePagination = false,
    hideLoading = false,
    hideAdd = false,
    noResultsMessage = 'No results found',
    onRefresh,
    onSort,
    onSetLimit,
    onGoToPage,
    onAdd,
    addButtonLabel = 'Create New',
    hideSearch = false,
    searchColumn,
    onSearch,
    customFilterButtons = [], // Default to an empty array if not provided
    staleTime = undefined,
    refetchInterval = undefined,
  } = props;

  // State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<limitT>(initialLimit);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null!);

  // Generate default sort options if none provided
  const defaultSortOptions = useMemo(() => {
    if (sortOptions.length > 0) return sortOptions;

    return columns.map(col => ({
      column: col.field,
      direction: 'desc' as const,
      label: col.header,
    })) as RpcSortOptionT<F>[];
  }, [columns, sortOptions]);

  const [selectedSort, setSelectedSort] = useState<RpcSortOptionT<F>>(
    defaultSortOptions.find(opt => opt.default) ?? defaultSortOptions[0]
  );

  // Data Query
  const {
    data: response,
    isLoading,
    refetch,
    pagination,
  } = useRpcQuery({
    functionName,
    functionArgs: functionArgs as Database['public']['Functions'][F]['Args'],
    filters,
    page,
    limit,
    sortOption: selectedSort,
    enabled,
    searchTerm,
    searchColumn: searchColumn ?? undefined,
    ...(staleTime !== undefined && {staleTime}),
    ...(refetchInterval !== undefined && {refetchInterval}),
  });

  // Cast the data to the correct type
  const data = (response?.data ?? []) as RpcReturnTypeT<F>[];
  const totalPages = pagination.totalPages;

  // Handlers
  const handleRefresh = useCallback(() => {
    refetch();
    onRefresh?.();
  }, [refetch, onRefresh]);

  const handleSort = useCallback(
    (option: typeof selectedSort) => {
      setSelectedSort(option);
      onSort?.(option);
      setPage(1);
    },
    [onSort]
  );

  const handleSetLimit = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const newLimit = parseInt(e.target.value, 10) as limitT;
      if (!VALID_LIMITS.includes(newLimit)) return;

      setLimit(newLimit);
      onSetLimit?.(newLimit);
      setPage(1);
    },
    [onSetLimit]
  );

  const handlePrev = useCallback(() => {
    setPage(prev => Math.max(prev - 1, 1));
    onGoToPage?.(page - 1);
  }, [page, onGoToPage]);

  const handleNext = useCallback(() => {
    setPage(prev => Math.min(prev + 1, totalPages));
    onGoToPage?.(page + 1);
  }, [page, totalPages, onGoToPage]);

  const goToPage = useCallback(
    (newPage: number) => {
      const validPage = Math.min(Math.max(1, newPage), totalPages);
      setPage(validPage);
      onGoToPage?.(validPage);
    },
    [totalPages, onGoToPage]
  );

  const handlePageClick = useCallback(
    ({nextSelectedPage}: ReactPaginateClickEventT) => {
      const newPage = nextSelectedPage + 1;
      goToPage(newPage);
    },
    [goToPage]
  );

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    refetch,
    setLimit: (newLimit: limitT) => {
      setLimit(newLimit);
      setPage(1);
    },
    goToPage,
    nextPage: handleNext,
    prevPage: handlePrev,
  }));

  // Search Functions
  const searchData = useCallback(
    async (searchTerm: string) => {
      setSearchTerm(searchTerm);
      onSearch?.(searchTerm);
      setPage(1);

      // Reset if the search term is empty
      if (searchTerm === '') {
        setPage(1);
        refetch();
      }
    },
    [onSearch, refetch]
  );

  // Create a memoized debounced version of the search function
  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        searchData(searchTerm).catch(error => console.error(error));
      }, 750),
    [searchData]
  );

  // Wrapper function to maintain the type consistency and handle debounce time
  const handleSearchData = (searchTerm: string, debounceTime?: number) => {
    if (debounceTime !== undefined && debounceTime !== 750) {
      // If a custom debounce time is provided, create a new debounced function
      debounce((term: string) => {
        searchData(term).catch(error => console.error(error));
      }, debounceTime)(searchTerm);
    } else {
      // Use the memoized debounced function
      debouncedSearch(searchTerm);
    }
  };

  // Add maxWidthClass for consistent column widths
  const maxWidthClass = 'max-w-xs';

  return (
    <div className={twMerge('box', className)}>
      {/* Header Section */}
      {!(hideTitle && hideRefresh && hideSort && hideSearch) && (
        <div className="box-header flex-wrap justify-between">
          {!hideTitle && title ? <h1 className="box-title">{title}</h1> : <div className="grow"></div>}
          <div className="flex items-stretch gap-2">
            {/* Custom Filter Buttons */}
            {customFilterButtons.map((button, index) => (
              <button
                key={index}
                onClick={button.onClick}
                className={`ti-btn ti-btn-icon ti-btn-wave ${button.active ? `!bg-${button.color || 'primary'} !text-white` : `bg-${button.color || 'primary'}/10 text-${button.color || 'primary'}`} hover:border-${button.color || 'primary'} hover:bg-${button.color || 'primary'} hover:text-white`}
              >
                {button.label}
              </button>
            ))}
            {/* Refresh Button */}
            {!hideRefresh && (
              <button
                onClick={handleRefresh}
                className="ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.75rem] !w-[1.75rem] bg-primary/10 text-[0.8rem] text-primary hover:border-primary hover:bg-primary hover:text-white"
              >
                <i className="ri-refresh-line"></i>
              </button>
            )}
            {/* Search Input */}
            {!hideSearch && (
              <div className="me-3">
                <div className="search-form h-full">
                  <input
                    className="ti-form-control form-control-sm h-full"
                    type="text"
                    placeholder="Search Here"
                    aria-label=".form-control-sm example"
                    onChange={() => handleSearchData(searchInputRef.current.value)}
                    ref={searchInputRef}
                  />
                </div>
              </div>
            )}
            {/* Sort Dropdown */}
            {!hideSort && (
              <div className="hs-dropdown ti-dropdown">
                <button className="ti-btn ti-btn-primary !m-0 !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white">
                  Sort By <i className="ri-arrow-down-s-line ms-1 inline-block align-middle"></i>
                </button>
                <ul className="hs-dropdown-menu ti-dropdown-menu hidden" role="menu">
                  {defaultSortOptions.map((option, index) => (
                    <li key={index}>
                      <button
                        onClick={() => handleSort(option)}
                        className="ti-dropdown-item !px-[0.9375rem] !py-2 !text-[0.8125rem] !font-medium"
                      >
                        {option.label}
                        {option.column === selectedSort.column && <i className="ri-check-line ms-2 text-primary"></i>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Add Button */}
            {!hideAdd && (
              <div>
                <button
                  onClick={onAdd}
                  className="ti-btn ti-btn-primary !m-0 h-full !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white"
                >
                  {addButtonLabel}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table Body */}
      <div className="box-body">
        {isLoading && !hideLoading ? (
          <LoadingSpinningLogoComponent />
        ) : (
          <div className="table-responsive">
            <table className="table-hover table-bordered table-sm ti-striped-table table min-w-full whitespace-nowrap">
              <thead>
                <tr>
                  {actions && <th className="!text-end">Actions</th>}
                  {columns.map((column, index) => (
                    <th key={index} className="text-start">
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr className="border">
                    <td colSpan={columns.length + (actions ? 1 : 0)}>
                      <div className="text-center font-bold">{noResultsMessage}</div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border border-solid border-inherit hover:bg-gray-100 dark:border-defaultborder/10 dark:hover:bg-light"
                    >
                      {actions && (
                        <td>
                          <div className="flex flex-row items-center justify-center gap-2 text-[0.9375rem]">
                            {actions
                              .filter(action => action.isShown?.(row as RpcReturnTypeT<F>) ?? true)
                              .map(
                                (action, actionIndex) =>
                                  action.permission(row as RpcReturnTypeT<F>) && (
                                    <button
                                      key={actionIndex}
                                      onClick={() => action.handler(row as RpcReturnTypeT<F>)}
                                      className={`ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.75rem] !w-[1.75rem] !gap-0 text-[0.8rem] bg-${action.color}/10 text-${action.color} hover:bg-${action.color} hover:text-white hover:border-${action.color}`}
                                    >
                                      <i className={action.icon}></i>
                                    </button>
                                  )
                              )}
                          </div>
                        </td>
                      )}
                      {columns.map((column, colIndex) => (
                        <td className={`overflow-hidden ${maxWidthClass}`} key={colIndex}>
                          {column.render ? column.render(row as RpcReturnTypeT<F>) : String(row[column.field] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer Section */}
      <div className="box-footer">
        <div className="flex items-center justify-between">
          {!(hideShowing && hideLimitIcon && hideLimit) && (
            <div className="flex items-center text-sm text-gray-700 dark:text-gray-400">
              {!hideShowing && (
                <div>
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, response?.total ?? 0)} of{' '}
                  {response?.total ?? 0} entries
                </div>
              )}
              {!hideLimitIcon && <i className="bi bi-arrow-right ms-2 font-semibold"></i>}
              {!hideLimit && (
                <select
                  className="ti-form-control form-select-sm form-select ml-4"
                  value={limit}
                  onChange={handleSetLimit}
                >
                  {VALID_LIMITS.map((value, index) => (
                    <option key={index} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {!hidePagination && totalPages > 1 && (
            <div className="pagination-style-4">
              <ReactPaginate
                forcePage={page - 1}
                onClick={handlePageClick}
                pageCount={Math.max(1, totalPages)}
                pageRangeDisplayed={3}
                marginPagesDisplayed={2}
                renderOnZeroPageCount={null}
                className="ti-pagination select-none gap-1"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                activeLinkClassName="active"
                breakLabel={'...'}
                breakLinkClassName="page-item"
                nextClassName={clsx({'hidden disabled': page === totalPages})}
                nextLinkClassName="page-link"
                nextLabel={'Next'}
                previousClassName={clsx({'hidden disabled': page === 1})}
                previousLinkClassName="page-link"
                previousLabel={'Prev'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const DataTableRpc = React.forwardRef(DataTableRpcComponent) as <F extends RpcFunctionNameT>(
  props: RpcDataTablePropsT<F> & React.RefAttributes<DataTableRpcRefT>
) => React.ReactElement | null;
